import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import { css, cx } from '@emotion/css'
import {
  theme,
  expandFontTheme,
  IconWarning
} from '@contrast-security-inc/design-system-foundations'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { useRouteServers } from './use-route-servers'
import { maxServersShownByDefault } from './shared'
import {
  CellRenderer,
  LoadingListCellRenderer,
  CellContentProps
} from '../../legacy-design-system/grid'
import { CollapsibleListToggle } from '../../legacy-design-system/list'
import { RouteContext } from '../route-provider'
import { LoadingMaskRectangle } from '../../legacy-design-system/loading-masks'
import { getServerDrilldownRoute } from '../route'
import { environmentI18nConfig } from '../servers-grid'
import { readOnlyGridCss } from '../../legacy-design-system/text'
import {
  Modal,
  DialogBody,
  DialogContainer,
  DialogFooter,
  DialogHeader
} from '../../legacy-design-system/modal'
import { Route, RouteServer } from '../../graphql/schema/types/route'
import { ellipsis } from 'polished'
import { Button } from '@contrast-security-inc/design-system-buttons'

const serverLiCss = css`
  padding-bottom: 4px;
  line-height: 17px;
  z-index: 10;
`
const serverLinkBaseCss = css`
  display: block;
  width: fit-content;
  ${ellipsis('calc(95%)')}
`

const serverLinkDisabledCss = cx(serverLinkBaseCss, readOnlyGridCss)

const ServersModalError = () => {
  const intl = useIntl()

  return (
    <div
      className={css`
        display: flex;
        min-height: 100px;
        align-items: center;
        justify-content: center;
      `}
    >
      <IconWarning size="sm" color="red70" />
      <Text
        color="red70"
        className={css`
          ${expandFontTheme(theme.font.body)};
          padding-left: 8px;
        `}
      >
        {intl.formatMessage({ id: 'detail_apiRequestError' })}
      </Text>
    </div>
  )
}

const ServersModalLoading = () => (
  <div>
    {[...Array(4)].map((_, index) => (
      <div
        key={index}
        className={css`
          margin-bottom: 8px;
        `}
      >
        <LoadingMaskRectangle
          dataTestId={`servers-loading-mask-${index}`}
          height={24}
        />
      </div>
    ))}
  </div>
)

const ServersModal = ({
  applicationUuid,
  organizationUuid,
  routeHash,
  routeName,
  isOpen,
  onCancel
}) => {
  const intl = useIntl()
  const { isLoading, isError, data } = useRouteServers({
    organizationUuid,
    applicationUuid,
    routeHash
  })

  return (
    <Modal isOpen={isOpen} onRequestClose={onCancel}>
      <DialogContainer size="medium">
        <DialogHeader
          onCancel={onCancel}
          headerContainerClassName={css`
            font-weight: bold;
          `}
        >
          {intl.formatMessage({ id: 'title_route_servers' })}
        </DialogHeader>
        <DialogBody>
          <p
            className={css`
              word-break: break-all;
            `}
          >
            {intl.formatMessage(
              { id: 'detail_routeServersModalDescription' },
              { routeName, b: (msg) => <b>{msg}</b> }
            )}
          </p>
          {isError ? (
            <ServersModalError />
          ) : (
            <div
              className={css`
                max-height: 290px;
                overflow: auto;
              `}
            >
              {isLoading ? (
                <ServersModalLoading />
              ) : (
                <ul>
                  {data?.servers?.map((server) => (
                    <ServerLink
                      key={server.server_id}
                      organizationUuid={organizationUuid}
                      server={server}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>
            {intl.formatMessage({ id: 'action_close' })}
          </Button>
        </DialogFooter>
      </DialogContainer>
    </Modal>
  )
}

export const getServerCellRenderer =
  (): CellRenderer<Route> =>
  ({ rowData, getCellContentProps }) =>
    <ServerCell rowData={rowData} getCellContentProps={getCellContentProps} />

const ServerCellEmpty = () => {
  const intl = useIntl()

  return (
    <li className={serverLiCss}>
      <span className={serverLinkDisabledCss}>
        {intl.formatMessage({ id: 'title_serverName_routeServerUnknown' })}
      </span>
    </li>
  )
}

const ServerCell = ({
  rowData: { id, servers, signature, serversTotal = 0, app },
  getCellContentProps
}: {
  readonly rowData: Route
  readonly getCellContentProps: (props: {}) => CellContentProps
}) => {
  const [showServersModal, setShowServersModal] = React.useState(false)
  const { organizationID: organizationUuid } = useContext(RouteContext)
  const showToggle = serversTotal > maxServersShownByDefault

  return (
    <div
      {...getCellContentProps({
        className: css`
          margin-left: 9px;
          margin-top: 15px;
          margin-bottom: 15px;
          width: 100%;
        `
      })}
    >
      <ul
        className={css`
          list-style-type: none;
          padding: 0;
          margin: 0;
        `}
      >
        {' '}
        {servers.length === 0 ? (
          <ServerCellEmpty />
        ) : (
          servers.map((server) => (
            <ServerLink
              key={server.id}
              organizationUuid={organizationUuid}
              server={server}
              showEnv={false}
            />
          ))
        )}
      </ul>
      {showToggle && (
        <>
          <CollapsibleListToggle
            isExpanded={false}
            onToggle={() => setShowServersModal(true)}
            total={serversTotal}
          />
          {showServersModal && (
            <ServersModal
              applicationUuid={app.id}
              organizationUuid={organizationUuid}
              routeHash={id}
              routeName={signature}
              isOpen={showServersModal}
              onCancel={() => setShowServersModal(false)}
            />
          )}
        </>
      )}
    </div>
  )
}

function ServerLink({
  organizationUuid,
  server: { id, server_id = '', name, environment },
  showEnv = true
}: {
  readonly organizationUuid?: string
  readonly server: RouteServer
  readonly showEnv?: boolean
}) {
  const serverId = id || server_id
  const intl = useIntl()
  const env = showEnv
    ? ` (${intl.formatMessage({ id: environmentI18nConfig[environment] })})`
    : ''
  const href = organizationUuid
    ? getServerDrilldownRoute(organizationUuid, serverId)
    : ''

  return (
    <li
      key={serverId}
      className={css`
        padding-bottom: 4px;
        line-height: 17px;
        z-index: 10;
      `}
    >
      <Link color="neutral100" className={serverLinkBaseCss} href={href}>
        {`${name}${env}`}
      </Link>
    </li>
  )
}

export function loadingServerCellRenderer() {
  return <LoadingListCellRenderer max={2} />
}
